import { Phone, Mail, MapPin, Clock, MessageCircle, Sparkles } from "lucide-react";
import { PALETTES, FONTS } from "../../lib/siteThemes";

const wa = (n) => `https://wa.me/${String(n).replace(/\D/g, "").replace(/^0/, "27")}`;

export const SiteRenderer = ({ site, compact = false }) => {
  const p = PALETTES[site.theme?.palette] || PALETTES.sunset;
  const f = FONTS[site.theme?.font] || FONTS.modern;
  const s = site.sections || {};
  const h = { fontFamily: f.heading, letterSpacing: "-0.02em" };
  const ph = site.photos || [];
  const c = s.contact || {};
  return (
    <div style={{ background: p.bg, color: p.ink }} className="min-h-full" data-testid="site-renderer">
      <nav className="sticky top-0 z-20 flex items-center gap-6 px-6 py-4 backdrop-blur-md" style={{ background: `${p.bg}E6` }}>
        <span className="text-xl font-bold" style={h}>{site.business_name}</span>
        <div className={`ml-auto gap-4 text-xs font-semibold sm:text-sm ${compact ? "hidden" : "flex overflow-x-auto"}`}>
          {["about", "services", "gallery", "contact"].map((k) => <a key={k} href={`#${k}`} className="capitalize opacity-80 hover:opacity-100">{k}</a>)}
        </div>
      </nav>
      <section className={`relative flex items-end overflow-hidden ${compact ? "min-h-[380px]" : "min-h-[78vh]"}`}>
        {ph[0] && <img src={ph[0]} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 10%, ${p.ink}E6 95%)` }} />
        <div className="relative max-w-3xl px-6 pb-14 text-white sm:px-12">
          {site.tagline && <p className="mb-3 inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest" style={{ background: p.accent, color: p.ink }}>{site.tagline}</p>}
          <h1 className={`${compact ? "text-4xl" : "text-4xl sm:text-6xl"} font-bold leading-[1.05]`} style={h} data-testid="site-hero-headline">{s.hero?.headline}</h1>
          <p className="mt-4 max-w-xl text-base opacity-90 sm:text-lg">{s.hero?.subheadline}</p>
          <a href="#contact" className="mt-7 inline-block rounded-full px-7 py-3 text-sm font-bold transition-transform hover:-translate-y-0.5" style={{ background: p.primary, color: "#fff" }}>{s.hero?.cta_label || "Contact us"}</a>
        </div>
      </section>
      <section id="about" className={`grid items-center gap-10 px-6 py-16 ${compact ? "" : "sm:px-12 md:grid-cols-2"}`}>
        <div>
          <h2 className="text-3xl font-bold sm:text-4xl" style={h}>{s.about?.title}</h2>
          {(s.about?.body || "").split("\n\n").map((t, i) => <p key={i} className="mt-4 leading-relaxed opacity-80">{t}</p>)}
        </div>
        {ph[1] && <img src={ph[1]} alt="" className="aspect-[4/3] w-full rounded-3xl object-cover" loading="lazy" />}
      </section>
      <section id="services" className="px-6 py-16 sm:px-12" style={{ background: `${p.primary}0D` }}>
        <h2 className="text-3xl font-bold sm:text-4xl" style={h}>{s.services?.title}</h2>
        <div className={`mt-8 grid gap-5 ${compact ? "" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {(s.services?.items || []).map((it, i) => (
            <div key={i} className="rounded-3xl bg-white p-6 shadow-sm transition-transform hover:-translate-y-1">
              <span className="grid h-10 w-10 place-items-center rounded-2xl" style={{ background: p.accent, color: p.ink }}><Sparkles className="h-5 w-5" /></span>
              <h3 className="mt-4 text-lg font-bold" style={h}>{it.name}</h3>
              <p className="mt-1.5 text-sm opacity-75">{it.description}</p>
            </div>
          ))}
        </div>
      </section>
      <section id="gallery" className="px-6 py-16 sm:px-12">
        <h2 className="text-3xl font-bold sm:text-4xl" style={h}>{s.gallery?.title || "Gallery"}</h2>
        <div className={`mt-8 grid grid-cols-2 gap-3 ${compact ? "" : "md:grid-cols-4"}`}>
          {ph.map((u, i) => <img key={i} src={u} alt="" loading="lazy" className={`h-48 w-full rounded-2xl object-cover md:h-60 ${i % 3 === 0 ? "md:col-span-2" : ""}`} />)}
        </div>
      </section>
      <section id="contact" className="px-6 py-16 sm:px-12" style={{ background: p.ink, color: "#fff" }}>
        <h2 className="text-3xl font-bold sm:text-4xl" style={h}>{c.title || "Get in touch"}</h2>
        <p className="mt-2 max-w-xl opacity-80">{c.body}</p>
        <div className={`mt-8 grid gap-4 text-sm ${compact ? "" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
          {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2"><Phone className="h-4 w-4" style={{ color: p.accent }} />{c.phone}</a>}
          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: p.accent }} />{c.email}</a>}
          {c.address && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" style={{ color: p.accent }} />{c.address}</span>}
          {c.hours && <span className="flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: p.accent }} />{c.hours}</span>}
        </div>
        {c.whatsapp && <a href={wa(c.whatsapp)} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white" data-testid="site-whatsapp-btn"><MessageCircle className="h-4 w-4" />Chat on WhatsApp</a>}
      </section>
      <footer className="px-6 py-5 text-center text-xs" style={{ background: p.ink, color: "#ffffff99", borderTop: "1px solid #ffffff1a" }}>
        © {new Date().getFullYear()} {site.business_name} · <a href="/website-builder" className="font-semibold underline" data-testid="site-made-with-link">Free website by HostAfricair.com</a>
      </footer>
    </div>
  );
};
